import { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { noop } from '@ajaibid/utils';
import { API_SUCCESS_CODE } from '@config/constanta';
import { AlertContext } from '@context/alert';
import useLayout from '@context/layout/useLayout';

import stockService from '@services/stock';
import { postStockWithdraw, putStockAutoOrderCancel } from '@services/api/stock/trading';
import { postDtWithdraw } from '@services/api/stock/dayTrading';
import { postMtWithdraw, putMtAutoOrderCancel } from '@services/api/stock/marginTrading';

import { CONST_DT_CUSTOM_ERROR_CODES } from '../../constants';
// import { useTracker } from '../../tracker';

const makeLimitOrderPayload = (orderDetail) => ({
  ticker_code: orderDetail.code,
  orderno: orderDetail.order_id,
});

const makeLimitOrderPayloadDt = (orderDetail) => ({
  ticker_code: orderDetail.code,
  order_no: orderDetail.order_id,
});

function useSubmitWithdraw({ orderDetail, onSuccess = noop, onDtError } = {}) {
  const { code } = orderDetail;

  const layout = useLayout();
  const queryClient = useQueryClient();
  const { showAlert } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);

  const isAccountRegular = orderDetail.account_type === 'REGULAR';
  const isAccountDT = orderDetail.account_type === 'DAY_TRADING';
  const isAccountMT = orderDetail.account_type === 'MARGIN_TRADING';
  const isLimitOrder = orderDetail.order_type === 'LIMIT';
  const isAutoOrder = orderDetail.order_type === 'AUTO';

  const { isTradeable: isDtTradeable } = stockService.detail.dt.tradeable.useDtTradeable(code);
  const { isTradeable: isMtTradeable } = stockService.detail.mt.tradeable.useMtTradeable(code);
  const { refetch: refetchTradelimit } = stockService.account.regular.tradelimit.useTradelimit();
  const { refetch: refetchDtTradelimit } = stockService.account.dt.tradelimit.useDtTradelimit({
    code,
    enabled: isDtTradeable, // Only call API when stock is DT tradeable
  });
  const { refetch: refetchMtTradelimit } = stockService.account.mt.tradelimit.useMtTradelimit({
    code,
    enabled: isMtTradeable, // Only call API when stock is MT tradeable
  });
  const { refetch: refetchPortfolio } =
    stockService.account.regular.portfolio.usePortfolioByCode(code);
  const { refetch: refetchDtPortfolio } =
    stockService.account.dt.portfolio.useDtPortfolioByCode(code);
  const { refetch: refetchMtPortfolio } =
    stockService.account.mt.portfolio.useMtPortfolioByCode(code);

  // const { eventOrderSendFail, eventOrderSendSuccess } = useTracker();

  const makeApiCall = async (post, postParams, funcParams = {}) => {
    const {
      errorCodeKey = 'err_code',
      errorMessageKey = 'err_message',
      successMessage = 'Berhasil membatalkan order',
      defaultErrorMessage = 'Gagal membatalkan order',
      refetchData = () => {},
    } = funcParams;

    setIsLoading(true);
    const response = await post(...postParams);

    const isSuccess = response?.[errorCodeKey] === API_SUCCESS_CODE;
    const isDtError = CONST_DT_CUSTOM_ERROR_CODES.includes(response?.[errorCodeKey]);
    switch (true) {
      case isSuccess:
        // For success, show success message as an alert
        // eventOrderSendSuccess();
        showAlert(successMessage, 'success');
        refetchData();
        queryClient.setQueriesData({ queryKey: ['v3/stock/orderlist-filter'] }, (existingData) => {
          if (!Array.isArray(existingData)) {
            return existingData;
          }
          const orderId = String(orderDetail?.order_id ?? '');
          return existingData.map((orderItem) => {
            if (String(orderItem?.order_no ?? '') !== orderId) {
              return orderItem;
            }

            return {
              ...orderItem,
              status: '4',
              status_description: 'Withdrawn',
              is_cancel: '0',
              is_amend: '0',
            };
          });
        });
        queryClient.invalidateQueries(['v3/stock/orderlist-filter']);
        onSuccess();
        break;
      case isDtError:
        // For DT-specific error, handle differently
        // eventOrderSendFail();
        refetchData();
        onDtError(response?.[errorCodeKey]);
        break;
      default:
        // For other error, show error message as an alert
        // eventOrderSendFail();
        showAlert(response?.[errorMessageKey] || defaultErrorMessage, 'error');
        refetchData();
        break;
    }
    setIsLoading(false);

    // Refetch data on order open & order history
    layout.bottomPanel.refetchTable();
  };

  const submitWithdraw = () => {
    switch (true) {
      case isLimitOrder && isAccountRegular: {
        const payload = makeLimitOrderPayload(orderDetail);
        makeApiCall(postStockWithdraw, [payload], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'statusMessage',
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isLimitOrder && isAccountDT: {
        const payload = makeLimitOrderPayloadDt(orderDetail);
        makeApiCall(postDtWithdraw, [payload], {
          refetchData: () => {
            refetchDtTradelimit();
            refetchDtPortfolio();
          },
        });
        break;
      }
      case isLimitOrder && isAccountMT: {
        const payload = makeLimitOrderPayload(orderDetail);
        makeApiCall(postMtWithdraw, [payload], {
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountRegular: {
        // param 1: Order ID, param 2: None
        makeApiCall(putStockAutoOrderCancel, [orderDetail.order_id, {}], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountMT: {
        // param 1: Order ID, param 2: None
        makeApiCall(putMtAutoOrderCancel, [orderDetail.order_id, {}], {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  return { isLoading, submitWithdraw };
}

export default useSubmitWithdraw;
